import Model from '@ember-data/model';
import { belongsTo } from 'ember-data/relationships';
import attr from 'ember-data/attr';
import { computed } from '@ember/object';
import { equal, or, gt } from '@ember/object/computed';
import { modelApiAction } from 'cio/utils/model-api-actions';
import { htmlSafe } from '@ember/template';
import SegmentModel from 'cio/models/segment';
import SqlImportModel, { SQLImportIdFieldName } from 'cio/models/sql-import';
import { ImportType, SqlImportDatabase } from 'cio/utils/sql-import';
import SqlImportDatabaseModel from './sql-import-database';

export enum SqlImportRowStatus {
  querying = 'querying',
  importing = 'importing',
  done = 'done',
  failed = 'failed',
  canceled = 'canceled',
  partiallyFailed = 'partiallyFailed',
  skipped = 'skipped',
}

const STATUS_DETAILS: Record<SqlImportRowStatus, { classNames: string; label: string }> = {
  [SqlImportRowStatus.querying]: {
    classNames: 'is-pending',
    label: 'Querying…',
  },
  [SqlImportRowStatus.importing]: {
    classNames: 'is-pending',
    label: 'Importing…',
  },
  [SqlImportRowStatus.done]: {
    classNames: 'is-success',
    label: 'Completed',
  },
  [SqlImportRowStatus.failed]: {
    classNames: 'is-error',
    label: 'Failed',
  },
  [SqlImportRowStatus.canceled]: {
    classNames: 'is-pending',
    label: 'Canceled',
  },
  [SqlImportRowStatus.partiallyFailed]: {
    classNames: 'is-warning',
    label: 'Some rows failed',
  },
  [SqlImportRowStatus.skipped]: {
    classNames: 'is-error',
    label: 'Skipped',
  },
};

export default class SqlImportRowModel extends Model {
  @attr('number') createdAt!: number;
  @attr('number') updatedAt!: number;
  @attr('number') endedAt!: number;

  @attr('string') query!: string;
  @attr('string') dbHostAndName!: string;

  @attr('boolean') addPeople!: boolean;
  @attr('boolean') updatePeople!: boolean;
  @attr('string') idFieldName!: SQLImportIdFieldName;
  @belongsTo('segment', { async: false, inverse: null }) segment?: SegmentModel;
  @belongsTo('sql-import', { async: true, inverse: 'sqlImportRows' }) import!: SqlImportModel;

  @attr('number') rows!: number;
  @attr('number') addedRows!: number;
  @attr('number') updatedRows!: number;
  @attr('number') failedRows!: number;
  @attr('number') errorExportId!: number;
  @attr('string') importFile!: string;
  @attr('string') location!: string;

  @attr('string') error!: string;
  @attr('string') status!: SqlImportRowStatus;
  @attr('string', { defaultValue: ImportType.Workspace }) importType!: ImportType | 'relationship';

  @equal('status', SqlImportRowStatus.done) isDone!: boolean;
  @equal('status', SqlImportRowStatus.failed) isFailed!: boolean;
  @equal('status', SqlImportRowStatus.skipped) isSkipped!: boolean;
  @equal('status', SqlImportRowStatus.importing) isImporting!: boolean;
  @equal('status', SqlImportRowStatus.querying) isQuerying!: boolean;
  @equal('status', SqlImportRowStatus.canceled) isCanceled!: boolean;
  @gt('errorExportId', 0) hasErrorFile!: boolean;
  @or('isImporting', 'isQuerying') isPending!: boolean;

  @computed('status', 'rows')
  get info() {
    if (this.isFailed && !this.addedRows && !this.updatedRows) {
      return STATUS_DETAILS[SqlImportRowStatus.failed];
    }
    if (this.isFailed) {
      return STATUS_DETAILS[SqlImportRowStatus.partiallyFailed];
    }
    return STATUS_DETAILS[this.status];
  }

  @computed('dbHostAndName', 'import.database.{type,project}', 'location')
  get databaseName() {
    let sqlImport = this.belongsTo('import' as any).value() as SqlImportModel | null;
    let database = sqlImport?.belongsTo('database').value() as SqlImportDatabaseModel | null;

    if (database?.type === SqlImportDatabase.bigQuery) {
      return htmlSafe(
        `<strong class="font-medium">${database?.projectId}</strong> in <strong class="font-medium">${this.location}</strong>`,
      );
    }

    if (!this.dbHostAndName) {
      return '';
    }

    let [, name] = this.dbHostAndName.split(':');
    return name ?? '';
  }

  get syncTarget() {
    let sqlImport = (this as SqlImportRowModel).belongsTo('import').value() as SqlImportModel;

    return sqlImport.syncTarget.toLowerCase();
  }

  cancel = modelApiAction({
    path: `canceled`,
    type: 'PUT',
    after(response) {
      return response.sql_import_row;
    },
  });
}

declare module 'ember-data/types/registries/model' {
  export default interface ModelRegistry {
    'sql-import-row': SqlImportRowModel;
  }
}
